import * as React from 'react';
import styled from 'styled-components';
import ArrowIndicator from './images/arrow.inline.svg';
import { PlainButton } from '../styled-components';
import { cssClampValue } from '../../utils/common';

const Wrapper = styled.div`
  position: relative;
  margin-right: calc(-1 * var(--earth-fm--container--padding));

  @media (min-width: 576px) {
    margin-right: calc(
      -1 *
        (100vw + 2 * var(--earth-fm--container--padding) - var(--earth-fm--container--max-width)) /
        2
    );
  }
`;

const Scrollable = styled.div<{ $scrollEndFaded: boolean }>`
  display: flex;
  // space for scrollbar
  margin-bottom: -10px;
  padding-bottom: 10px;
  gap: ${cssClampValue(10, 20)};
  flex-wrap: nowrap;
  overflow-x: auto;
  scroll-behavior: smooth;
  mask-image: ${({ $scrollEndFaded }) =>
    $scrollEndFaded ? 'linear-gradient(90deg,#000 calc(100% - 100px),transparent)' : 'none'};

  &:focus {
    padding-top: 1px;
    padding-left: 1px;
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
  }

  // add space at the end of the scroll
  &::after {
    content: '';
    width: 1px;
  }
`;

const ScrollControlButton = styled(PlainButton)<{
  $hideButtonInMobile: boolean;
  $small: boolean;
}>`
  display: ${({ $hideButtonInMobile }) => ($hideButtonInMobile ? 'none' : 'flex')};
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  width: ${({ $small }) => ($small ? '50px' : '76px')};
  height: ${({ $small }) => ($small ? '50px' : '76px')};
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background-color: var(--earth-fm--color--green);
  border-radius: 50%;

  img {
    pointer-events: none;
  }

  @media (min-width: 992px) {
    display: flex;
  }
`;

interface Props {
  children: React.ReactNode;
  className?: string;
  hideButtonInMobile?: boolean;
  isDefaultContentVisible?: boolean;
  smallButton?: boolean;
  title?: string;
  isScrollEndFaded?: boolean;
}

export default function ScrollableRow({
  children,
  className = undefined,
  hideButtonInMobile = true,
  isDefaultContentVisible = false,
  smallButton = false,
  title = undefined,
  isScrollEndFaded = false,
}: Props) {
  const [scrollEndFaded, setScrollEndFaded] = React.useState(isScrollEndFaded);
  const [hasScroll, setHasScroll] = React.useState(false);
  const [isScrollEnd, setIsScrollEnd] = React.useState(false);

  const scrollableRef = React.useRef<HTMLDivElement>(null);

  const handleClick = React.useCallback(() => {
    if (scrollableRef.current === null) {
      return;
    }
    const scrollable = scrollableRef.current;
    scrollable.scrollTo({
      top: 0,
      left: scrollable.scrollLeft + (scrollableRef.current.clientWidth / 12) * 4,
      behavior: 'smooth',
    });
  }, []);

  const moveFocusToSearchInput = React.useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      const searchInput = document.getElementById('header-search-field');
      searchInput?.focus();
    }
  }, []);

  React.useEffect(() => {
    if (isDefaultContentVisible) {
      // if user presses arrow up or shift + tab key on the first element of the default search content
      // move focus back to the search input
      const firstElement = scrollableRef.current?.firstElementChild as HTMLDivElement;
      firstElement?.addEventListener('keydown', moveFocusToSearchInput);
      return () => {
        firstElement?.removeEventListener('keydown', moveFocusToSearchInput);
      };
    }
    return () => {};
  }, [isDefaultContentVisible, moveFocusToSearchInput]);

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const scrollable = event.target as HTMLDivElement;
      const buttonHalfWidth = smallButton ? 25 : 38;

      if (
        // hide shadow and arrow button when scroll reach the end
        scrollable.scrollLeft >
        scrollable.scrollWidth - scrollable.clientWidth - buttonHalfWidth
      ) {
        if (isScrollEndFaded) {
          setScrollEndFaded(false);
        }
        setIsScrollEnd(true);
      } else {
        if (isScrollEndFaded) {
          setScrollEndFaded(true);
        }
        setIsScrollEnd(false);
      }
    },
    [isScrollEndFaded, smallButton],
  );

  React.useEffect(() => {
    // if scrollable content is wider than the container,
    // show the arrow indicator. Otherwise hide it.
    setTimeout(() => {
      if (
        scrollableRef.current &&
        scrollableRef.current.scrollWidth > scrollableRef.current.clientWidth
      ) {
        setHasScroll(true);
      }
    }, 200);
  }, [scrollableRef]);

  return (
    <Wrapper className={className}>
      <Scrollable
        ref={scrollableRef}
        className={`${className}__scrollable default-content-${isDefaultContentVisible}`}
        aria-label={title}
        $scrollEndFaded={scrollEndFaded}
        onScroll={(e) => handleScroll(e)}
      >
        {children}
      </Scrollable>
      {hasScroll && !isScrollEnd && (
        <ScrollControlButton
          className={`${className}__scrollable__button`}
          onClick={handleClick}
          $small={smallButton}
          $hideButtonInMobile={hideButtonInMobile}
          tabIndex={-1}
          aria-label="Vertical scroll button"
        >
          <ArrowIndicator />
        </ScrollControlButton>
      )}
    </Wrapper>
  );
}
